import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project import
import ProfileTab from './ProfileTab';
import SettingTab from './SettingTab';
import Avatar from '@components/@extended/Avatar';
import MainCard from '@components/MainCard';
import Transitions from '@components/@extended/Transitions';

// assets
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import avatar1 from '../../../../../assets/images/users/avatar-1.png';
import { useSelector, useDispatch } from 'react-redux'
import { userLoaderAction } from '../../../../../redux/authReducer'
import { uploadUrl } from '../../../../../consts/api'
import { profile } from '@services/user'
import { apiUrl } from '@services/axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../../providers/auth';
// tab panel wrapper
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} {...other}>
      {value === index && children}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `profile-tab-${index}`,
    'aria-controls': `profile-tabpanel-${index}`
  };
}

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function Profile() {
  const [user, setUser] = useState({})
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const reduxUser = useSelector((state) => state.auth?.data?.userData);
  const dispatch = useDispatch()

  const resolveAssetUrl = (path) => {
    if (!path) return avatar1;
    if (/^https?:\/\//.test(path)) return path;
    const baseUrl = (apiUrl || '').replace(/\/api\/?$/, '').replace(/\/$/, '');
    return `${baseUrl}${path}`;
  };

  const avatarSrc = useMemo(() => {
    const src = resolveAssetUrl(user?.avatar);
    if (!user?.avatar || src === avatar1) return src;
    return `${src}${src.includes('?') ? '&' : '?'}v=${user?.updated_at || Date.now()}`;
  }, [user?.avatar, user?.updated_at]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    profile()
      .then(res => {
        const userData = res.data.data;
        setUser(userData)
        dispatch(userLoaderAction({ user: userData }))
      })
      .catch(err => {
        console.log(err)
      })
  }, [dispatch, isAuthenticated])

  useEffect(() => {
    if (reduxUser?.id) {
      setUser(reduxUser);
    }
  }, [reduxUser]);

  const theme = useTheme();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const iconBackColorOpen = 'grey.100';

  if (!isAuthenticated) {
    return (
      <ButtonBase
        onClick={() => navigate('/login')}
        sx={{
          px: 1.5,
          py: 0.75,
          borderRadius: 1,
          color: 'primary.main',
          fontWeight: 600,
          '&:hover': { bgcolor: 'primary.lighter' }
        }}
      >
        Đăng nhập
      </ButtonBase>
    );
  }

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <ButtonBase
        sx={{
          p: 0.25,
          bgcolor: open ? iconBackColorOpen : 'transparent',
          borderRadius: 1,
          '&:hover': { bgcolor: 'secondary.lighter' },
          '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.dark}`, outlineOffset: 2 }
        }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ p: 0.5 }}>


          <Avatar alt="profile user" src={avatarSrc} size="sm" />

          <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
            {typeof (user) === 'undefined' ? '' : user.name}
          </Typography>
        </Stack>
      </ButtonBase>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={{ boxShadow: theme.customShadows.z1, width: 290, minWidth: 240, maxWidth: { xs: 250, md: 290 } }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 3 }}>
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Grid item>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Avatar alt="profile user" src={avatarSrc} sx={{ width: 32, height: 32 }} />
                          <Stack>
                            <Typography variant="h6">{typeof (user) !== 'undefined' ? user.name : ''}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {user?.detail?.position || user?.position_name || ''}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>

                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs variant="fullWidth" value={value} onChange={handleChange} aria-label="profile tabs">
                      <Tab
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          alignItems: 'center',
                          textTransform: 'capitalize'
                        }}
                        icon={<UserOutlined style={{ marginBottom: 0, marginRight: '10px' }} />}
                        label="Cá nhân"
                        {...a11yProps(0)}
                      />
                      <Tab
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          alignItems: 'center',
                          textTransform: 'capitalize'
                        }}
                        icon={<SettingOutlined style={{ marginBottom: 0, marginRight: '10px' }} />}
                        label="Thông tin"
                        {...a11yProps(1)}
                      />
                    </Tabs>
                  </Box>
                  <TabPanel value={value} index={0} dir={theme.direction}>
                    <ProfileTab />
                  </TabPanel>
                  <TabPanel value={value} index={1} dir={theme.direction}>
                    <SettingTab />
                  </TabPanel>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}

TabPanel.propTypes = { children: PropTypes.node, value: PropTypes.number, index: PropTypes.number, other: PropTypes.any };
